import { createGlobalStyle } from "styled-components";
import { device } from "../../lib/breakpoints";

/**
 * Profile/account Ant Design dropdown (menu is portaled to document.body).
 * Use with Dropdown overlayClassName="clumse-profile-menu" (Ant Design 5; popupClassName is not wired for Dropdown).
 *
 * Square corners + slate border match the black industrial nav (not the generic
 * .ant-dropdown-menu 8px radius in styles.css).
 *
 * Padding lives on .ant-dropdown-menu-title-content so it applies whether the
 * label is a <Link> (a) or a nested span[role="button"] (industrial nav logout).
 */
export const ProfileDropdownMenuStyles = createGlobalStyle`
  /* Outer popup - Ant Design 5 applies radius on the root as well as the menu. */
  .clumse-profile-menu.ant-dropdown {
    border-radius: 0 !important;
    box-shadow: none !important;
    padding: 0 !important;
    background: transparent !important;
  }

  .clumse-profile-menu.ant-dropdown .ant-dropdown-menu,
  .clumse-profile-menu .ant-dropdown-menu,
  .clumse-profile-menu .ant-dropdown-menu.ant-dropdown-menu-root {
    background: #ffffff !important;
    border: 1px solid #1e293b !important;
    border-radius: 0 !important;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14) !important;
    padding: 4px 0 !important;
  }

  .clumse-profile-menu .ant-dropdown-menu-item,
  .clumse-profile-menu .ant-dropdown-menu-submenu-title {
    border-radius: 0 !important;
  }

  .clumse-profile-menu .ant-dropdown-menu-item {
    text-align: left !important;
    justify-content: flex-start !important;
    color: #1e293b !important;
    height: auto !important;
    line-height: 1.45 !important;
    padding-block: 0 !important;
    padding-inline: 8px !important;
    margin-block: 0 !important;
  }

  .clumse-profile-menu .ant-dropdown-menu-item-divider {
    margin: 2px 8px !important;
    border-color: #e2e8f0 !important;
  }

  .clumse-profile-menu .ant-dropdown-menu-item button {
    text-align: left !important;
    justify-content: flex-start !important;
    width: 100% !important;
  }

  /* Hit area: Ant Design wraps labels in .ant-dropdown-menu-title-content */
  .clumse-profile-menu .ant-dropdown-menu-item .ant-dropdown-menu-title-content {
    display: flex !important;
    align-items: center !important;
    gap: 0.5rem !important;
    width: 100% !important;
    box-sizing: border-box !important;
    padding: 12px 16px !important;
    min-height: 44px !important;
  }

  .clumse-profile-menu .ant-dropdown-menu-item a {
    display: flex !important;
    align-items: center !important;
    gap: 0.5rem !important;
    padding: 0 !important;
    margin: 0 !important;
    flex: 1 1 auto !important;
    min-width: 0 !important;
    color: #1e293b !important;
    text-decoration: none !important;
    transition: background 0.15s ease, color 0.15s ease !important;
  }

  .clumse-profile-menu .ant-dropdown-menu-item .ant-dropdown-menu-title-content span[role="button"] {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 0.5rem !important;
    width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    text-align: left !important;
    color: #1e293b !important;
    cursor: pointer !important;
  }

  .clumse-profile-menu
    .ant-dropdown-menu-item:hover:not(:has(a.active))
    .ant-dropdown-menu-title-content {
    background: #f1f5f9 !important;
  }

  .clumse-profile-menu .ant-dropdown-menu-item a:hover {
    color: #0f172a !important;
    background: transparent !important;
  }

  .clumse-profile-menu .ant-dropdown-menu-item a.active {
    color: #ff5722 !important;
    background: transparent !important;
  }

  .clumse-profile-menu .ant-dropdown-menu-item:has(a.active) .ant-dropdown-menu-title-content {
    background: #fff7ed !important;
  }

  .clumse-profile-menu .ant-dropdown-menu-item .ant-dropdown-menu-title-content span[role="button"]:hover {
    color: #0f172a !important;
    background: transparent !important;
  }

  /* Tablet/desktop: center row content (matches nav breakpoint where this dropdown is used). */
  @media ${device.minTablet} {
    .clumse-profile-menu.ant-dropdown .ant-dropdown-menu,
    .clumse-profile-menu .ant-dropdown-menu,
    .clumse-profile-menu .ant-dropdown-menu.ant-dropdown-menu-root {
      padding: 8px 0 !important;
      box-sizing: border-box !important;
    }

    .clumse-profile-menu .ant-dropdown-menu-item-divider {
      margin: 4px 10px !important;
    }

    .clumse-profile-menu .ant-dropdown-menu-item,
    .clumse-profile-menu .ant-dropdown-menu-submenu-title {
      text-align: center !important;
      justify-content: center !important;
      padding-inline: 10px !important;
    }

    .clumse-profile-menu .ant-dropdown-menu-item .ant-dropdown-menu-title-content {
      justify-content: center !important;
      text-align: center !important;
      padding: 12px 20px !important;
    }

    .clumse-profile-menu .ant-dropdown-menu-item a {
      justify-content: center !important;
      text-align: center !important;
    }

    .clumse-profile-menu .ant-dropdown-menu-item .ant-dropdown-menu-title-content span[role="button"] {
      justify-content: center !important;
      text-align: center !important;
    }

    .clumse-profile-menu .ant-dropdown-menu-item .anticon {
      color: inherit !important;
    }

    /* Beat global styles.css blue hover on dropdown items. */
    .clumse-profile-menu .ant-dropdown-menu-item:hover a,
    .clumse-profile-menu
      .ant-dropdown-menu-item:hover
      .ant-dropdown-menu-title-content
      span[role="button"],
    .clumse-profile-menu .ant-dropdown-menu-item:hover .anticon {
      color: #0f172a !important;
    }
  }

  /* Log out: same slate as Profile (no error / orange treatment). */
  .clumse-profile-menu .ant-dropdown-menu-item:last-child a,
  .clumse-profile-menu
    .ant-dropdown-menu-item:last-child
    .ant-dropdown-menu-title-content
    span[role="button"] {
    color: #1e293b !important;
    cursor: pointer;
  }

  .clumse-profile-menu .ant-dropdown-menu-item:last-child a:hover,
  .clumse-profile-menu
    .ant-dropdown-menu-item:last-child
    .ant-dropdown-menu-title-content
    span[role="button"]:hover {
    color: #0f172a !important;
    background: transparent !important;
  }

  .clumse-profile-menu .ant-dropdown-menu-item:last-child .anticon {
    color: #1e293b !important;
  }

  .clumse-profile-menu .ant-dropdown-menu-item:last-child a:hover .anticon,
  .clumse-profile-menu
    .ant-dropdown-menu-item:last-child
    .ant-dropdown-menu-title-content
    span[role="button"]:hover
    .anticon {
    color: #0f172a !important;
  }
`;
